import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, LogOut, Check, X, Receipt, PieChart, ChevronRight } from 'lucide-react';
import axios from 'axios';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';

const api = axios.create({ baseURL: '/api/pm-portal' });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('pm_portal_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
api.interceptors.response.use((r) => r, (err) => {
  if (err.response?.status === 401) {
    localStorage.removeItem('pm_portal_token');
    localStorage.removeItem('pm_portal_account');
    window.location.href = '/participant/login';
  }
  return Promise.reject(err);
});

const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtD = (d) => { if (!d) return ''; try { return format(parseISO(String(d).slice(0, 10)), 'd MMM yyyy'); } catch { return String(d); } };
// Participant-friendly invoice status wording — no back-office jargon.
const LABEL = {
  extract_generated: 'Being checked', created: 'Being checked', approved: 'Approved',
  claimed: 'Claimed from NDIA', paid: 'Paid', provider_paid: 'Provider paid',
  on_hold: 'On hold', declined: 'Declined', cancelled: 'Cancelled',
};
const TONE = {
  approved: 'bg-blue-100 text-blue-700', claimed: 'bg-indigo-100 text-indigo-700',
  paid: 'bg-emerald-100 text-emerald-700', provider_paid: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-red-100 text-red-700', cancelled: 'bg-red-100 text-red-700', on_hold: 'bg-amber-100 text-amber-700',
};

// Standalone participant dashboard — live plan budget, category spend, invoices, and
// one-tap approve/decline of anything the plan manager sent for approval.
export default function ParticipantDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    api.get('/overview').then((r) => setData(r.data)).catch(() => setData({ plans: [], byCategory: [], invoices: [], pending: [] }));
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('pm_portal_token')) { navigate('/participant/login', { replace: true }); return; }
    api.get('/me').then((r) => setMe(r.data)).catch(() => {});
    load();
  }, [navigate, load]);

  // Read-only invoice detail — participants can open ANY of their invoices, not just pending ones.
  const [detail, setDetail] = useState(null);
  const openInvoice = async (id) => {
    if (!id) return;
    try { const r = await api.get(`/invoices/${id}`); setDetail(r.data); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not open the invoice'); }
  };

  const decide = async (id, decision) => {
    setBusy(id + decision);
    try {
      await api.post(`/invoices/${id}/approval`, { decision });
      toast.success(decision === 'approved' ? 'Approved — your plan manager will arrange payment.' : 'Declined — your plan manager will follow up with you.');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Something went wrong'); }
    finally { setBusy(''); }
  };

  const logout = () => {
    localStorage.removeItem('pm_portal_token');
    localStorage.removeItem('pm_portal_account');
    navigate('/participant/login', { replace: true });
  };

  const plans = data?.plans || [];
  const cats = (data?.byCategory || []).filter((c) => c.amount > 0);
  const catMax = Math.max(1, ...cats.map((c) => c.amount));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow">
              <Wallet size={18} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-slate-900 text-sm">NDIS Budget Portal</p>
              {me && <p className="text-xs text-slate-400">{me.client_name}{me.ndis_number ? ` · NDIS ${me.ndis_number}` : ''}</p>}
            </div>
          </div>
          <button onClick={logout} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-500 transition-colors"><LogOut size={15} /> Logout</button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Welcome */}
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-2xl p-5 text-white shadow-lg">
          <p className="text-sm opacity-80">Hi {me?.name || me?.first_name || 'there'}</p>
          <p className="text-xl font-bold mt-0.5">Your NDIS plan at a glance</p>
          {plans.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <span><span className="opacity-70">Budget</span> <strong>{money(plans.reduce((s, p) => s + Number(p.budget || 0), 0))}</strong></span>
              <span><span className="opacity-70">Remaining</span> <strong>{money(plans.reduce((s, p) => s + Number(p.remaining || 0), 0))}</strong></span>
            </div>
          )}
        </div>

        {/* Pending approvals — the action items, always on top */}
        {(data?.pending || []).length > 0 && (
          <div className="bg-white rounded-2xl border border-amber-200 overflow-hidden">
            <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 text-sm font-bold text-amber-800">
              {data.pending.length} invoice{data.pending.length === 1 ? '' : 's'} waiting for your OK
            </div>
            <div className="divide-y divide-slate-50">
              {data.pending.map((p) => (
                <div key={p.id} className="p-4 flex flex-wrap items-center gap-3">
                  <button onClick={() => openInvoice(p.id)} className="min-w-0 flex-1 text-left group">
                    <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-indigo-700">{p.provider || 'Provider'}</p>
                    <p className="text-xs text-slate-400">{p.provider_invoice_number ? `#${p.provider_invoice_number} · ` : ''}{fmtD(p.invoice_date)}{p.line_count ? ` · ${p.line_count} item${p.line_count === 1 ? '' : 's'}` : ''} · <span className="text-indigo-600 font-semibold">view details</span></p>
                  </button>
                  <span className="text-base font-bold text-slate-800 tabular-nums">{money(p.total_amount)}</span>
                  <div className="flex gap-2">
                    <button onClick={() => decide(p.id, 'approved')} disabled={!!busy}
                      className="px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1 disabled:opacity-50">
                      <Check size={13} /> Approve
                    </button>
                    <button onClick={() => decide(p.id, 'declined')} disabled={!!busy}
                      className="px-3.5 py-2 rounded-xl border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs font-bold flex items-center gap-1 disabled:opacity-50">
                      <X size={13} /> Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Plan budgets */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h2 className="text-sm font-bold text-slate-700 flex items-center gap-1.5 mb-4"><Wallet size={15} className="text-indigo-500" /> Plan budget</h2>
          {!data ? <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>
            : plans.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">No active plan on file yet.</p>
              : plans.map((p, i) => {
                const budget = Number(p.budget || 0);
                const remaining = Number(p.remaining || 0);
                const usedPct = budget > 0 ? Math.min(100, Math.max(0, ((budget - remaining) / budget) * 100)) : 0;
                return (
                  <div key={p.id || i} className={clsx('py-3', i > 0 && 'border-t border-slate-50')}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="text-slate-600 font-medium">Plan budget</span>
                      <span className="tabular-nums font-semibold text-slate-800">{money(budget)}</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className={clsx('h-full rounded-full', remaining < 0 ? 'bg-rose-500' : usedPct > 85 ? 'bg-amber-400' : 'bg-indigo-500')} style={{ width: `${usedPct}%` }} />
                    </div>
                    <div className="flex items-center justify-between text-xs mt-1.5">
                      <span className="text-slate-400">{usedPct.toFixed(0)}% used</span>
                      <span className={clsx('font-semibold tabular-nums', remaining < 0 ? 'text-rose-600' : 'text-emerald-600')}>{money(remaining)} left</span>
                    </div>
                  </div>
                );
              })}
        </div>

        {/* Where the money went (last 90 days) */}
        {cats.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-1.5 mb-4"><PieChart size={15} className="text-indigo-500" /> Where the money went <span className="text-slate-400 font-normal">(last 90 days)</span></h2>
            <div className="space-y-2.5">
              {cats.map((c) => (
                <div key={c.category || c.label}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-600">{c.label}</span>
                    <span className="tabular-nums font-semibold text-slate-700">{money(c.amount)}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-violet-400" style={{ width: `${(c.amount / catMax) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent invoices */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 text-sm font-bold text-slate-700 flex items-center gap-1.5"><Receipt size={15} className="text-indigo-500" /> Recent invoices</div>
          {!data ? <p className="text-sm text-slate-400 py-8 text-center">Loading…</p>
            : (data.invoices || []).length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">No invoices in the last 90 days.</p>
              : (
                <div className="divide-y divide-slate-50">
                  {data.invoices.map((v, i) => (
                    <button key={v.id || i} onClick={() => openInvoice(v.id)} className="w-full px-5 py-3 flex items-center gap-3 text-left hover:bg-slate-50 transition group">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-700 truncate group-hover:text-indigo-700">{v.provider_display || 'Provider'}</p>
                        <p className="text-[11px] text-slate-400">{v.provider_invoice_number ? `#${v.provider_invoice_number} · ` : ''}{fmtD(v.invoice_date)}</p>
                      </div>
                      <span className="text-sm font-semibold tabular-nums text-slate-700">{money(v.total_amount)}</span>
                      <span className={clsx('text-[10px] font-bold px-2 py-1 rounded-full', TONE[v.status] || 'bg-slate-100 text-slate-600')}>{LABEL[v.status] || v.status}</span>
                      <ChevronRight size={14} className="text-slate-300 group-hover:text-indigo-400 shrink-0" />
                    </button>
                  ))}
                </div>
              )}
        </div>

        <p className="text-center text-[11px] text-slate-400 pb-6">Questions about anything here? Contact your plan manager — they can see the same numbers.</p>
      </div>

      {/* Invoice detail — read-only view of any invoice; approve/decline included when it's pending */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 p-0 sm:p-4" onMouseDown={() => setDetail(null)}>
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[88vh] overflow-y-auto" onMouseDown={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-base font-bold text-slate-800 truncate">{detail.provider || 'Provider'}</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {detail.provider_invoice_number ? `Invoice #${detail.provider_invoice_number} · ` : ''}{fmtD(detail.invoice_date)}
                  {(detail.service_date_from || detail.service_date_to) ? ` · services ${fmtD(detail.service_date_from)}${detail.service_date_to ? ` – ${fmtD(detail.service_date_to)}` : ''}` : ''}
                </p>
              </div>
              <button onClick={() => setDetail(null)} aria-label="Close" className="text-slate-400 hover:text-slate-600 shrink-0"><X size={20} /></button>
            </div>
            <div className="px-5 py-4">
              <div className="flex items-center justify-between mb-3">
                <span className={clsx('text-[10px] font-bold px-2 py-1 rounded-full', TONE[detail.status] || 'bg-slate-100 text-slate-600')}>{LABEL[detail.status] || detail.status}</span>
                <span className="text-xl font-bold text-slate-800 tabular-nums">{money(detail.total_amount)}</span>
              </div>
              <div className="rounded-xl border border-slate-100 overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 text-[10px] font-bold uppercase tracking-wide text-slate-400">What's on this invoice</div>
                {(detail.lines || []).length === 0 ? <p className="px-3 py-4 text-xs text-slate-400">No line details available.</p> : (
                  <div className="divide-y divide-slate-50">
                    {detail.lines.map((l, i) => (
                      <div key={i} className="px-3 py-2.5 flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-slate-700">{l.name || 'Support item'}</p>
                          <p className="text-[10px] text-slate-400">{fmtD(l.service_date_from || l.item_date)}{(l.service_date_to && String(l.service_date_to).slice(0, 10) !== String(l.service_date_from || l.item_date || '').slice(0, 10)) ? ` to ${fmtD(l.service_date_to)}` : ''}{Number(l.qty) !== 1 ? ` · ${Number(l.qty)} × ${money(l.unit_price)}` : ''}</p>
                        </div>
                        <span className="text-xs font-semibold tabular-nums text-slate-700 shrink-0">{money(l.line_total)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {detail.approval_status === 'pending' && ['extract_generated', 'created', 'approved'].includes(detail.status) && (
                <div className="flex gap-2 mt-4">
                  <button onClick={() => { const id = detail.id; setDetail(null); decide(id, 'approved'); }} disabled={!!busy}
                    className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-50">✓ Approve</button>
                  <button onClick={() => { const id = detail.id; setDetail(null); decide(id, 'declined'); }} disabled={!!busy}
                    className="flex-1 py-2.5 rounded-xl border border-rose-200 text-rose-600 hover:bg-rose-50 text-sm font-bold disabled:opacity-50">✕ Decline</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
